import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { graphql } from 'app/generated/gql'
import type { CarestackDashboardQuery } from 'app/generated/gql/graphql'
import type { GroupBy } from 'app/components/viz/DateGroupSelector'
import { opsClient } from 'app/lib/opsUrqlClient'
import { logError } from 'lib/logging'

const CARESTACK_DASHBOARD_QUERY = graphql(`
  query CarestackDashboard($period: String!) {
    carestackDashboard(period: $period) {
      base64
      workspaces {
        workspaceId
        workspaceName
      }
    }
  }
`)

export type WorkspaceItem = CarestackDashboardQuery['carestackDashboard']['workspaces'][number]

export type UseCarestackDashboardReturn = {
  base64: string
  workspaces: WorkspaceItem[]
  groupBy: GroupBy
  onGroupChange: (group: GroupBy) => void
}

export function useCarestackDashboard(): UseCarestackDashboardReturn {
  const [searchParams, setSearchParams] = useSearchParams()
  const [groupBy, setGroupBy] = useState<GroupBy>((searchParams.get(`period`) as GroupBy) || `months`)
  const [base64, setBase64] = useState(``)
  const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([])

  useEffect(() => {
    setSearchParams({ period: groupBy })
  }, [groupBy])

  useEffect(() => {
    opsClient
      .query<CarestackDashboardQuery>(CARESTACK_DASHBOARD_QUERY, { period: groupBy })
      .toPromise()
      .then(({ data, error }) => {
        if (error) {
          logError(`Error fetching carestack dashboard`, error.message)
          return
        }
        setBase64(data?.carestackDashboard.base64 ?? ``)
        setWorkspaces(data?.carestackDashboard.workspaces ?? [])
      })
      .catch((err) => logError(`Error fetching carestack dashboard`, err))
  }, [groupBy])

  return {
    base64,
    workspaces,
    groupBy,
    onGroupChange: setGroupBy,
  }
}
